import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { isSailingCrewFinderEnabled } from '../../utils/moduleUtils';
import { canPostToCrewFinder, crewFinderPostTypeLabel } from '../../utils/sailingCrewFinder';
import EditButton from '../../../ui/EditButton';

function isOpen(post = {}) {
  return String(post.status || 'open') === 'open';
}

export default function SailingCrewFinderCard({
  teamId,
  userId,
  modules = {},
  memberTags = [],
  isAdmin = false,
  onOpen,
}) {
  const enabled = isSailingCrewFinderEnabled(modules);
  const [openPosts, setOpenPosts] = useState([]);
  const [myPosts, setMyPosts] = useState([]);

  useEffect(() => {
    if (!enabled || !teamId || !userId) {
      setOpenPosts([]);
      setMyPosts([]);
      return undefined;
    }
    const postsRef = collection(db, 'teams', teamId, 'sailingCrewFinderPosts');
    const unsubscribeOpen = safeOnSnapshot(query(postsRef, where('status', '==', 'open')), (snapshot) => {
      setOpenPosts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    }, undefined, { label: 'sailing-crew-finder-open', teamId });
    const unsubscribeMine = safeOnSnapshot(query(postsRef, where('createdBy', '==', userId)), (snapshot) => {
      setMyPosts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    }, undefined, { label: 'sailing-crew-finder-member', teamId });
    return () => {
      if (typeof unsubscribeOpen === 'function') unsubscribeOpen();
      if (typeof unsubscribeMine === 'function') unsubscribeMine();
    };
  }, [enabled, teamId, userId]);

  const posts = useMemo(() => {
    const byId = new Map();
    openPosts.forEach((post) => byId.set(post.id, post));
    myPosts.forEach((post) => byId.set(post.id, post));
    return Array.from(byId.values());
  }, [myPosts, openPosts]);

  const summary = useMemo(() => posts.reduce((acc, post) => {
    if (!isOpen(post)) return acc;
    if (post.type === 'available_to_crew') acc.available += 1;
    else acc.needed += 1;
    if (post.createdBy === userId) acc.mine += 1;
    return acc;
  }, { needed: 0, available: 0, mine: 0 }), [posts, userId]);

  const canPostCrewNeeded = canPostToCrewFinder({ modules, memberTags, isAdmin, type: 'crew_needed' });
  const canPostAvailability = canPostToCrewFinder({ modules, memberTags, isAdmin, type: 'available_to_crew' });
  const latest = posts.filter(isOpen).slice(0, 3);

  if (!enabled || !teamId || !userId) return null;

  return (
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">Crew Finder</span>
        {isAdmin && <EditButton section="sailingCrewFinder" label="Configure" onEdit={() => onOpen?.({ initialTab: 'admin', isAdmin: true })} />}
      </div>
      <div className="card-body">
        <div className="availability-status-list">
          <div className="availability-status-item"><span>Crew needed</span><span className="availability-pill available">{summary.needed}</span></div>
          <div className="availability-status-item"><span>Available crew</span><span className="availability-pill info">{summary.available}</span></div>
          <div className="availability-status-item"><span>Your posts</span><span className="availability-pill unavailable">{summary.mine}</span></div>
        </div>

        <div className="form-actions" style={{ marginTop: 12 }}>
          {canPostCrewNeeded && (
            <button type="button" className="btn btn--primary" onClick={() => onOpen?.({ initialTab: 'crew_needed' })}>
              Request crew
            </button>
          )}
          {canPostAvailability && (
            <button type="button" className={canPostCrewNeeded ? 'btn btn--secondary' : 'btn btn--primary'} onClick={() => onOpen?.({ initialTab: 'available_to_crew' })}>
              Share availability
            </button>
          )}
          {!canPostCrewNeeded && !canPostAvailability && (
            <button type="button" className="btn btn--primary" onClick={() => onOpen?.({ initialTab: 'my_posts' })}>
              Open Crew Finder
            </button>
          )}
        </div>
        {!canPostCrewNeeded && !canPostAvailability && (
          <p className="text-muted" style={{ marginTop: 10 }}>
            Posting is limited by the club&apos;s Crew Finder settings.
          </p>
        )}

        {latest.length === 0 ? <p className="text-muted" style={{ marginTop: 12 }}>No open Crew Finder posts yet.</p> : (
          <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
            {latest.map((post) => (
              <button key={post.id} type="button" className="list-row" style={{ textAlign: 'left' }} onClick={() => onOpen?.({ initialTab: post.type === 'available_to_crew' ? 'available_to_crew' : 'crew_needed' })}>
                <strong>{post.title || crewFinderPostTypeLabel(post.type)}</strong>
                <small className="text-muted" style={{ display: 'block' }}>
                  {[crewFinderPostTypeLabel(post.type), post.boatLabelSnapshot || '', post.createdByNameSnapshot || 'Member'].filter(Boolean).join(' · ')}
                </small>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
